import React, { useState, useCallback, useMemo, memo, useEffect, ReactNode } from 'react';
import * as XLSX from 'xlsx';

// Type definitions
interface HelmetProps {
  children: React.ReactElement;
}

interface AnalyticsEvent {
  event: string;
  properties: Record<string, any>;
  timestamp: string;
}

interface ConversionOptions {
  header: boolean;
  delimiter: string;
  dynamicTyping: boolean;
  trimValues: boolean;
  skipEmptyLines: boolean;
  headerColor: string;
  autoFitColumns: boolean;
  freezeFirstRow: boolean;
  addBorders: boolean;
}

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

interface AnalyticsHook {
  track: (event: string, properties?: Record<string, any>) => void;
}

interface SEOData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterCard: string;
}

interface AnalyticsStats {
  total: number;
  conversions: number;
  uploads: number;
}

// Mock react-helmet-async for SEO
const Helmet: React.FC<HelmetProps> = ({ children }) => {
  useEffect(() => {
    // Extract props from children
    const extractPropsFromChildren = (element: React.ReactElement): any => {
      if (element.props?.children) {
        const childArray = React.Children.toArray(element.props.children);
        const helmetData: any = { meta: [], link: [] };
        
        childArray.forEach((child: any) => {
          if (React.isValidElement(child)) {
            const childElement = child as React.ReactElement<any>;
            
            if (childElement.type === 'title') {
              helmetData.title = childElement.props.children;
            } else if (childElement.type === 'meta') {
              helmetData.meta.push(childElement.props);
            } else if (childElement.type === 'link') {
              helmetData.link.push(childElement.props);
            } else if (childElement.type === 'script') {
              // Handle JSON-LD script
              if (childElement.props.type === 'application/ld+json') {
                // Create and append script element
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.textContent = childElement.props.children;
                document.head.appendChild(script);
              }
            }
          }
        });
        return helmetData;
      }
      return {};
    };

    const helmetData = extractPropsFromChildren(children);
    
    if (helmetData.title) {
      document.title = helmetData.title;
    }
    
    const updateMeta = (name: string, content: string): void => {
      let meta = document.querySelector(`meta[name="${name}"]`) || 
                 document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('twitter:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (helmetData.meta && Array.isArray(helmetData.meta)) {
      helmetData.meta.forEach((meta: any) => {
        if (meta.name) updateMeta(meta.name, meta.content);
        if (meta.property) updateMeta(meta.property, meta.content);
      });
    }

    if (helmetData.link && Array.isArray(helmetData.link)) {
      helmetData.link.forEach((link: any) => {
        let linkEl = document.querySelector(`link[rel="${link.rel}"]`) as HTMLLinkElement;
        if (!linkEl) {
          linkEl = document.createElement('link') as HTMLLinkElement;
          linkEl.rel = link.rel;
          document.head.appendChild(linkEl);
        }
        Object.keys(link).forEach((attr) => {
          if (attr !== 'rel') linkEl.setAttribute(attr, link[attr]);
        });
      });
    }
  }, [children]);

  return null;
};

// Analytics hook
const useAnalytics = (): AnalyticsHook => {
  const track = useCallback((event: string, properties: Record<string, any> = {}): void => {
    console.log('Analytics Event:', event, properties);
    
    // Google Analytics 4
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event, properties);
    }
    
    // Track to localStorage for demo
    const analytics: AnalyticsEvent[] = JSON.parse(localStorage.getItem('csvConverter_analytics') || '[]');
    analytics.push({
      event,
      properties,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('csvConverter_analytics', JSON.stringify(analytics.slice(-100)));
  }, []);

  return { track };
};

// Theme context
const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || 
             (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return true;
  });

  const toggleTheme = useCallback((): void => {
    setIsDark((prev) => {
      const newTheme = !prev;
      localStorage.setItem('theme', newTheme ? 'dark' : 'light');
      return newTheme;
    });
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = (): ThemeContextType => {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Optimized CSV parser
const parseCSVLine = (line: string, delimiter: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (i + 1 < line.length && line[i + 1] === '"') {
        currentValue += '"';
        i++;
      } else {
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      values.push(currentValue);
      currentValue = '';
    } else {
      currentValue += char;
    }
  }
  
  values.push(currentValue);
  return values;
};

// Enhanced type conversion
const convertValueType = (value: string): any => {
  if (value === '') return null;
  
  const lowerValue = value.toLowerCase();
  
  if (lowerValue === 'true') return true;
  if (lowerValue === 'false') return false;
  if (lowerValue === 'null' || lowerValue === 'n/a' || lowerValue === 'na') return null;
  
  if (/^-?\d+$/.test(value)) return parseInt(value, 10);
  if (/^-?\d+\.\d+$/.test(value)) return parseFloat(value);
  if (/^-?\d+,\d+\.\d+$/.test(value)) return parseFloat(value.replace(',', ''));
  if (/^\$-?\d+(\.\d{2})?$/.test(value)) return parseFloat(value.replace('$', ''));
  if (/^-?\d+(\.\d+)?%$/.test(value)) return parseFloat(value.replace('%', '')) / 100;
  
  if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(.\d{3})?Z?)?$/.test(value)) {
    return new Date(value);
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [month, day, year] = value.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }
  
  return value;
};

// Enhanced CSV to worksheet conversion
const csvToWorksheet = (csv: string, options: ConversionOptions): XLSX.WorkSheet => {
  const lines = csv.split(/\r?\n/);
  
  if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === '')) {
    return XLSX.utils.aoa_to_sheet([['No data']]);
  }
  
  const data: any[][] = [];
  let headers: string[] = [];
  let startRow = 0;
  
  if (options.header && lines.length > 0) {
    headers = parseCSVLine(lines[0], options.delimiter)
      .map((header) => options.trimValues ? header.trim() : header);
    data.push([...headers]);
    startRow = 1;
  }
  
  for (let i = startRow; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (options.skipEmptyLines && line === '') continue;
    
    const values = parseCSVLine(line, options.delimiter);
    
    if (values.length > 0) {
      const rowData = values.map((value) => {
        const processedValue = options.trimValues ? value.trim() : value;
        return options.dynamicTyping ? convertValueType(processedValue) : processedValue;
      });
      
      data.push(rowData);
    }
  }
  
  const worksheet = XLSX.utils.aoa_to_sheet(data);
  
  // Apply styling to headers
  if (options.header && data.length > 0) {
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
    
    for(let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({r: 0, c: C});
      if (!worksheet[cellAddress]) continue;
      
      worksheet[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: options.headerColor || "EFEFEF" } },
        border: {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" }
        }
      };
    }
  }
  
  // Auto-fit columns
  if (options.autoFitColumns) {
    const colWidths: number[] = [];
    data.forEach((row) => {
      row.forEach((cell, colIndex) => {
        const cellLength = String(cell || '').length;
        colWidths[colIndex] = Math.max(colWidths[colIndex] || 10, cellLength + 2);
      });
    });
    worksheet['!cols'] = colWidths.map((width) => ({ width: Math.min(width, 50) }));
  }
  
  return worksheet;
};

// Analytics Dashboard Component
const AnalyticsDashboard: React.FC = memo(() => {
  const [analytics, setAnalytics] = useState<AnalyticsEvent[]>([]);
  const { isDark } = useTheme();
  
  useEffect(() => {
    const data: AnalyticsEvent[] = JSON.parse(localStorage.getItem('csvConverter_analytics') || '[]');
    setAnalytics(data);
  }, []);
  
  const stats: AnalyticsStats = useMemo(() => {
    const total = analytics.length;
    const conversions = analytics.filter((a) => a.event === 'csv_converted').length;
    const uploads = analytics.filter((a) => a.event === 'file_uploaded').length;
    
    return { total, conversions, uploads };
  }, [analytics]);
  
  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Usage Analytics
      </h3>
      <div className="grid grid-cols-3 gap-4">
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
            {stats.total}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Total Events
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
            {stats.conversions}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Conversions
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
            {stats.uploads}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            File Uploads
          </div>
        </div>
      </div>
    </div>
  );
});

AnalyticsDashboard.displayName = 'AnalyticsDashboard';

// Main Component (without ThemeProvider)
const CsvToExcelContent: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const { track } = useAnalytics();
  
  // States
  const [csvInput, setCsvInput] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [fileName, setFileName] = useState<string>('data');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [isExcelGenerated, setIsExcelGenerated] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  
  // Advanced options
  const [hasHeader, setHasHeader] = useState<boolean>(true);
  const [delimiter, setDelimiter] = useState<string>(',');
  const [dynamicTyping, setDynamicTyping] = useState<boolean>(true);
  const [trimValues, setTrimValues] = useState<boolean>(true);
  const [skipEmptyLines, setSkipEmptyLines] = useState<boolean>(true);
  const [sheetName, setSheetName] = useState<string>('Sheet1');
  const [excelFormat, setExcelFormat] = useState<'xlsx' | 'xls' | 'ods'>('xlsx');
  
  // New advanced options
  const [headerColor, setHeaderColor] = useState<string>('EFEFEF');
  const [autoFitColumns, setAutoFitColumns] = useState<boolean>(true);
  const [freezeFirstRow, setFreezeFirstRow] = useState<boolean>(false);
  const [addBorders, setAddBorders] = useState<boolean>(true);
  const [compressionLevel, setCompressionLevel] = useState<number>(6);
  const [chunkSize, setChunkSize] = useState<number>(1000);
  
  // SEO data
  const seoData: SEOData = useMemo(() => ({
    title: "Free CSV to Excel Converter Online - Convert CSV Files to XLSX, XLS, ODS",
    description: "Convert CSV files to Excel format online for free. Support for XLSX, XLS, and ODS formats. Advanced features include custom delimiters, data type conversion, and bulk processing.",
    keywords: "CSV to Excel converter, CSV to XLSX, CSV to XLS, online converter, free CSV converter, spreadsheet converter, data conversion tool",
    canonical: "https://yoursite.com/csv-to-excel-converter",
    ogTitle: "CSV to Excel Converter - Free Online Tool",
    ogDescription: "Convert your CSV files to Excel format instantly. No registration required. Supports multiple formats and advanced conversion options.",
    ogImage: "https://yoursite.com/og-csv-converter.jpg",
    twitterCard: "summary_large_image"
  }), []);

  // Handle input change with performance optimization
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setCsvInput(e.target.value);
    setError('');
    setIsExcelGenerated(false);
    setSuccessMessage('');
    track('csv_input_changed', { length: e.target.value.length });
  }, [track]);

  // Handle file upload with drag and drop
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement> | { target: { files: FileList } }): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    track('file_uploaded', { 
      fileName: file.name, 
      fileSize: file.size,
      fileType: file.type 
    });
    
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop();
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }
    
    const reader = new FileReader();
    reader.onload = (event): void => {
      if (event.target?.result) {
        setCsvInput(event.target.result as string);
        setError('');
        setIsExcelGenerated(false);
        setSuccessMessage('');
      }
    };
    reader.onerror = (): void => {
      setError('Error reading the file');
      track('file_upload_error', { fileName: file.name });
    };
    reader.readAsText(file);
  }, [track]);

  // Enhanced example data
  const useExampleData = useCallback((): void => {
    const exampleCsv = `Name,Age,Email,IsActive,JoinDate,Score,Salary,Department
John Doe,32,john@example.com,true,2021-03-15,85.5,$75000,Engineering
Jane Smith,28,jane@example.com,true,2020-11-20,92.0,$68000,Marketing
Bob Johnson,45,bob@example.com,false,2019-07-08,76.8,$82000,Sales
Alice Brown,33,alice@example.com,true,2022-01-10,88.2,$71000,Engineering
Charlie Wilson,,charlie@example.com,false,2020-02-28,,$65000,Support
Sarah Davis,29,sarah@example.com,true,2023-05-12,94.5,$73000,Design`;

    setCsvInput(exampleCsv);
    setError('');
    setIsExcelGenerated(false);
    setSuccessMessage('');
    track('example_data_used');
  }, [track]);

  // Enhanced conversion with progress tracking
  const convertCsvToExcel = useCallback(async (): Promise<void> => {
    setError('');
    setIsLoading(true);
    setIsExcelGenerated(false);
    setSuccessMessage('');
    
    if (!csvInput.trim()) {
      setError('Please enter CSV data');
      setIsLoading(false);
      return;
    }

    try {
      const startTime = Date.now();
      
      const options: ConversionOptions = {
        header: hasHeader,
        delimiter,
        dynamicTyping,
        trimValues,
        skipEmptyLines,
        headerColor,
        autoFitColumns,
        freezeFirstRow,
        addBorders
      };
      
      // Process in chunks for large files
      const lines = csvInput.split('\n');
      if (lines.length > chunkSize) {
        track('large_file_processing', { lines: lines.length, chunkSize });
      }
      
      const worksheet = csvToWorksheet(csvInput, options);
      const workbook = XLSX.utils.book_new();
      
      // Set workbook properties
      workbook.Props = {
        Title: fileName,
        Subject: "CSV to Excel Conversion",
        Author: "CSV Converter Tool",
        CreatedDate: new Date()
      };
      
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      
      // Apply freeze panes if enabled
      if (freezeFirstRow && hasHeader) {
        worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };
      }
      
      // Set compression level for XLSX
      const writeOptions: XLSX.WritingOptions = {
        bookType: excelFormat
      };
      
      // Note: XLSX compression is handled differently in newer versions
      if (excelFormat === 'xlsx') {
        // Compression is typically handled by the library internally
        (writeOptions as any).compression = compressionLevel;
      }
      
      XLSX.writeFile(workbook, `${fileName || 'data'}.${excelFormat}`, writeOptions);
      
      const processingTime = Date.now() - startTime;
      
      setIsExcelGenerated(true);
      setSuccessMessage(`Excel file "${fileName}.${excelFormat}" has been generated and downloaded!`);
      setIsLoading(false);
      
      track('csv_converted', {
        fileName: `${fileName}.${excelFormat}`,
        format: excelFormat,
        processingTime,
        rowCount: lines.length,
        hasHeaders: hasHeader
      });
      
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      setError(`Error converting CSV to Excel: ${errorMessage}`);
      setIsLoading(false);
      track('conversion_error', { error: errorMessage });
    }
  }, [csvInput, hasHeader, delimiter, dynamicTyping, trimValues, skipEmptyLines, fileName, sheetName, excelFormat, headerColor, autoFitColumns, freezeFirstRow, compressionLevel, chunkSize, track]);

  // Clear all
  const clearAll = useCallback((): void => {
    setCsvInput('');
    setError('');
    setFileName('data');
    setSheetName('Sheet1');
    setIsExcelGenerated(false);
    setSuccessMessage('');
    track('data_cleared');
  }, [track]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback((): void => {
    setIsFullscreen(!isFullscreen);
    track('fullscreen_toggled', { isFullscreen: !isFullscreen });
  }, [isFullscreen, track]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        handleFileUpload({ target: { files: files as any } });
      } else {
        setError('Please drop a CSV file');
      }
    }
  }, [handleFileUpload]);

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900' : 'bg-gray-50'} ${isFullscreen ? 'fixed inset-0 z-50 overflow-auto' : ''}`}>
      
      {/* SEO Meta Tags */}
      <Helmet>
        <>
          <title>{seoData.title}</title>
          <meta name="description" content={seoData.description} />
          <meta name="keywords" content={seoData.keywords} />
          <link rel="canonical" href={seoData.canonical} />
          
          {/* Open Graph */}
          <meta property="og:title" content={seoData.ogTitle} />
          <meta property="og:description" content={seoData.ogDescription} />
          <meta property="og:image" content={seoData.ogImage} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={seoData.canonical} />
          
          {/* Twitter */}
          <meta name="twitter:card" content={seoData.twitterCard} />
          <meta name="twitter:title" content={seoData.ogTitle} />
          <meta name="twitter:description" content={seoData.ogDescription} />
          <meta name="twitter:image" content={seoData.ogImage} />
          
          {/* Additional SEO */}
          <meta name="robots" content="index, follow" />
          <meta name="author" content="CSV Converter Tool" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
          
          {/* Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "CSV to Excel Converter",
              "description": seoData.description,
              "url": seoData.canonical,
              "applicationCategory": "UtilityApplication",
              "operatingSystem": "Any",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            })}
          </script>
        </>
      </Helmet>

      <div className={`${isFullscreen ? 'p-4' : 'max-w-7xl mx-auto p-6'}`}>
          
          {/* Header with Controls */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                CSV to Excel Converter
              </h1>
              <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Convert CSV files to Excel format with advanced options
              </p>
            </div>
            
            <div className="flex space-x-2">
              {/* Analytics Toggle */}
              <button
                onClick={() => setShowAnalytics(!showAnalytics)}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
                title="Toggle Analytics"
                type="button"
              >
                📊
              </button>
              
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
                title="Toggle Theme"
                type="button"
              >
                {isDark ? '☀️' : '🌙'}
              </button>
              
              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
                title="Toggle Fullscreen"
                type="button"
              >
                {isFullscreen ? '📱' : '🖥️'}
              </button>
            </div>
          </div>

          {/* Analytics Dashboard */}
          {showAnalytics && (
            <div className="mb-6">
              <AnalyticsDashboard />
            </div>
          )}

          {/* Description */}
          <div className={`mb-6 p-4 rounded-lg border ${isDark ? 'bg-green-900/20 border-green-800 text-green-200' : 'bg-green-50 border-green-100 text-green-800'}`}>
            <p>
              Convert CSV (Comma-Separated Values) data to Excel format. Upload a CSV file, paste your data, or drag and drop files.
              Supports XLSX, XLS, and ODS formats with advanced conversion options.
            </p>
          </div>
          
          <div className={`grid ${isFullscreen ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'} gap-6`}>
            
            {/* Input Section */}
            <div className="flex flex-col h-full">
              <div className={`p-4 rounded-lg shadow-sm mb-4 flex-grow border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="flex justify-between items-center mb-3">
                  <label className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    CSV Input
                  </label>
                  <div className="flex space-x-2">
                    <button
                      className={`text-sm transition-colors ${isDark ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-800'}`}
                      onClick={useExampleData}
                      type="button"
                    >
                      Use Example
                    </button>
                    <button
                      className={`text-sm transition-colors ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}`}
                      onClick={() => setCsvInput('')}
                      type="button"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                
                {/* File Upload with Drag & Drop */}
                <div 
                  className={`mb-4 p-4 border-2 border-dashed rounded-lg transition-colors ${isDark ? 'border-gray-600 hover:border-gray-500 bg-gray-700/50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'}`}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="fileInput"
                  />
                  <label 
                    htmlFor="fileInput"
                    className={`cursor-pointer block text-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                  >
                    <div className="mb-2">📁</div>
                    <div className="text-sm">
                      Click to upload or drag & drop CSV files here
                    </div>
                  </label>
                </div>
                
                <textarea
                  className={`w-full h-80 p-3 border rounded-lg font-mono text-sm transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-green-500 focus:border-green-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-green-500 focus:border-green-500'}`}
                  value={csvInput}
                  onChange={handleInputChange}
                  placeholder="Paste your CSV data here, e.g.,
Name,Age,Email
John Doe,30,john@example.com
Jane Smith,25,jane@example.com"
                />
              </div>
              
              {/* Configuration */}
              <div className={`p-4 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      Output File Name
                    </label>
                    <input
                      type="text"
                      className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-green-500 focus:border-green-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-green-500 focus:border-green-500'}`}
                      value={fileName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFileName(e.target.value)}
                      placeholder="Enter file name (without extension)"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      Worksheet Name
                    </label>
                    <input
                      type="text"
                      className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-green-500 focus:border-green-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-green-500 focus:border-green-500'}`}
                      value={sheetName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSheetName(e.target.value)}
                      placeholder="Enter worksheet name"
                    />
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Excel Format
                  </label>
                  <select
                    className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-green-500 focus:border-green-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-green-500 focus:border-green-500'}`}
                    value={excelFormat}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setExcelFormat(e.target.value as 'xlsx' | 'xls' | 'ods')}
                  >
                    <option value="xlsx">XLSX - Excel 2007+ Format (.xlsx)</option>
                    <option value="xls">XLS - Excel 97-2004 Format (.xls)</option>
                    <option value="ods">ODS - OpenDocument Spreadsheet (.ods)</option>
                  </select>
                </div>
                
                <div className="mb-4">
                  <button
                    className={`text-sm flex items-center transition-colors ${isDark ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-800'}`}
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    type="button"
                  >
                    <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                
                {showAdvanced && (
                  <div className={`space-y-4 p-3 rounded-lg mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    
                    {/* Basic Options */}
                    <div className="space-y-3">
                      <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Basic Options</h4>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="hasHeader"
                          checked={hasHeader}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHasHeader(e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <label htmlFor="hasHeader" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          First row contains headers
                        </label>
                      </div>
                      
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Delimiter
                        </label>
                        <select
                          className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          value={delimiter}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setDelimiter(e.target.value)}
                        >
                          <option value=",">Comma (,)</option>
                          <option value=";">Semicolon (;)</option>
                          <option value="\t">Tab</option>
                          <option value="|">Pipe (|)</option>
                        </select>
                      </div>
                    </div>

                    {/* Data Processing Options */}
                    <div className="space-y-3">
                      <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Data Processing</h4>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="dynamicTyping"
                          checked={dynamicTyping}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDynamicTyping(e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <label htmlFor="dynamicTyping" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Convert values to proper types (numbers, booleans, dates)
                        </label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="trimValues"
                          checked={trimValues}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrimValues(e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <label htmlFor="trimValues" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Trim whitespace from values
                        </label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="skipEmptyLines"
                          checked={skipEmptyLines}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSkipEmptyLines(e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <label htmlFor="skipEmptyLines" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Skip empty lines
                        </label>
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Chunk Size (for large files)
                        </label>
                        <input
                          type="number"
                          min="100"
                          max="10000"
                          className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          value={chunkSize}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setChunkSize(parseInt(e.target.value))}
                          placeholder="1000"
                        />
                      </div>
                    </div>

                    {/* Formatting Options */}
                    <div className="space-y-3">
                      <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Formatting Options</h4>
                      
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Header Background Color
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type="color"
                            value={`#${headerColor}`}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHeaderColor(e.target.value.substring(1))}
                            className="w-12 h-8 border rounded"
                          />
                          <input
                            type="text"
                            className={`flex-1 p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            value={headerColor}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHeaderColor(e.target.value)}
                            placeholder="EFEFEF"
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="autoFitColumns"
                          checked={autoFitColumns}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAutoFitColumns(e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <label htmlFor="autoFitColumns" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Auto-fit column widths
                        </label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="freezeFirstRow"
                          checked={freezeFirstRow}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFreezeFirstRow(e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <label htmlFor="freezeFirstRow" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Freeze first row (headers)
                        </label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="addBorders"
                          checked={addBorders}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddBorders(e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <label htmlFor="addBorders" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Add borders to cells
                        </label>
                      </div>
                    </div>

                    {/* Performance Options */}
                    <div className="space-y-3">
                      <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Performance Options</h4>
                      
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Compression Level (0-9)
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="9"
                          className="w-full"
                          value={compressionLevel}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompressionLevel(parseInt(e.target.value))}
                        />
                        <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Current: {compressionLevel} (0=fastest, 9=smallest file)
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="mt-4 flex justify-between">
                  <button
                    className={`px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      isDark 
                        ? 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500' 
                        : 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500'
                    }`}
                    onClick={convertCsvToExcel}
                    disabled={isLoading}
                    type="button"
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </div>
                    ) : (
                      'Convert to Excel'
                    )}
                  </button>
                  
                  <button
                    className={`px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      isDark 
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200 focus:ring-gray-500' 
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-800 focus:ring-gray-500'
                    }`}
                    onClick={clearAll}
                    type="button"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
            
            {/* Output Section */}
            <div className={`p-4 rounded-lg shadow-sm h-full flex flex-col border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-center mb-3">
                <label className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Excel Output
                </label>
              </div>
              
              <div className="flex-grow flex flex-col">
                {error && (
                  <div className={`border p-3 rounded-lg mb-3 ${isDark ? 'bg-red-900/20 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
                    <p className="font-medium">Error</p>
                    <p>{error}</p>
                  </div>
                )}
                
                {!error && !isExcelGenerated && (
                  <div className={`border p-4 rounded-lg flex-grow flex items-center justify-center ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                    <div className="text-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <p>
                        After conversion, Excel file will be downloaded automatically to your computer.
                      </p>
                    </div>
                  </div>
                )}
                
                {!error && isExcelGenerated && (
                  <div className="flex-grow flex flex-col">
                    <div className={`border p-4 rounded-lg mb-4 ${isDark ? 'bg-green-900/20 border-green-800 text-green-200' : 'bg-green-50 border-green-200 text-green-700'}`}>
                      <div className="flex items-center mb-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <p className="font-medium">Success!</p>
                      </div>
                      <p>{successMessage}</p>
                    </div>
                    
                    <div className={`border p-4 rounded-lg flex-grow ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                      <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Preview Information</h3>
                      <ul className={`space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        <li><strong>File Name:</strong> {fileName}.{excelFormat}</li>
                        <li><strong>Worksheet Name:</strong> {sheetName}</li>
                        <li><strong>Format:</strong> {excelFormat.toUpperCase()}</li>
                        <li><strong>Headers Included:</strong> {hasHeader ? 'Yes' : 'No'}</li>
                        <li><strong>Delimiter Used:</strong> {delimiter === ',' ? 'Comma' : delimiter === ';' ? 'Semicolon' : delimiter === '\t' ? 'Tab' : 'Pipe'}</li>
                        <li><strong>Data Rows:</strong> {csvInput.split('\n').length - (hasHeader ? 1 : 0)} rows</li>
                        <li><strong>Auto-fit Columns:</strong> {autoFitColumns ? 'Yes' : 'No'}</li>
                        <li><strong>Freeze Headers:</strong> {freezeFirstRow ? 'Yes' : 'No'}</li>
                      </ul>
                    </div>
                    
                    <div className="mt-4">
                      <button
                        className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                          isDark 
                            ? 'bg-green-600 hover:bg-green-700 text-white' 
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                        onClick={convertCsvToExcel}
                        type="button"
                      >
                        Convert Again
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Information Section */}
          <div className={`mt-8 p-6 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>About CSV to Excel Conversion</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>How This Converter Works</h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  This tool transforms CSV (Comma-Separated Values) data into Excel spreadsheet formats. CSV files are simple text files containing tabular data with values separated by a delimiter, typically a comma. The converter parses this data and generates a properly formatted Excel file that preserves data types, formatting, and structure.
                </p>
              </div>
              
              <div>
                <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Advanced Features</h3>
                <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  <li><strong>Multiple Excel Formats</strong> - Support for XLSX, XLS, and ODS formats</li>
                  <li><strong>Custom Styling</strong> - Header colors, borders, and formatting options</li>
                  <li><strong>Performance Optimization</strong> - Chunk processing for large files and compression control</li>
                  <li><strong>Data Type Detection</strong> - Automatic conversion of numbers, dates, booleans, and currency</li>
                  <li><strong>Advanced Layout</strong> - Auto-fit columns, freeze panes, and custom formatting</li>
                  <li><strong>Drag & Drop Support</strong> - Easy file upload with visual feedback</li>
                  <li><strong>Dark/Light Theme</strong> - Comfortable viewing in any lighting condition</li>
                  <li><strong>Fullscreen Mode</strong> - Distraction-free conversion experience</li>
                  <li><strong>Usage Analytics</strong> - Track your conversion history and patterns</li>
                  <li><strong>SEO Optimized</strong> - Structured data and meta tags for better discoverability</li>
                </ul>
              </div>
              
              <div>
                <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Performance Tips</h3>
                <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  <li>For files larger than 1000 rows, adjust the chunk size for optimal performance</li>
                  <li>Use higher compression levels for smaller file sizes (slower processing)</li>
                  <li>Disable auto-fit columns for very wide datasets to improve speed</li>
                  <li>Use XLSX format for the best balance of features and file size</li>
                  <li>Enable dynamic typing only when needed to preserve original data formats</li>
                </ul>
              </div>
              
              <div>
                <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Browser Compatibility</h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  This converter works in all modern browsers including Chrome, Firefox, Safari, and Edge. 
                  No server upload required - all processing happens locally in your browser for maximum privacy and speed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
};

// Wrapper component with ThemeProvider
const CsvToExcel: React.FC = () => {
  return (
    <ThemeProvider>
      <CsvToExcelContent />
    </ThemeProvider>
  );
};

export default CsvToExcel;